import React, { useEffect, useState, useRef } from "react";
import { CheckSquare, Plus, Trash2, Upload, ExternalLink, Inbox, Send, MessageSquare, Paperclip, X, Check, Clock, Users as UsersIcon, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { BACKEND_URL } from "@/lib/api";
import { useAuth, ROLE_LABELS } from "@/lib/auth.jsx";
import Layout, { PageHeader } from "@/components/Layout";
import { getNav } from "@/pages/navConfigs";
import { useSubmitting } from "@/lib/useSubmitting";

export default function TasksPage() {
  const { user, customRole } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [perms, setPerms] = useState({ can_assign_to: [] });
  const [view, setView] = useState("assigned-to-me");
  const [form, setForm] = useState({ title: "", description: "", assignee_ids: [], deadline: "", attachment_url: "" });
  const [openTaskId, setOpenTaskId] = useState(null);
  const taskSubmit = useSubmitting();

  const load = async () => {
    const [t, u, p] = await Promise.all([
      api.get("/tasks"),
      api.get("/users").catch(() => ({ data: [] })),
      api.get("/task-permissions"),
    ]);
    setTasks(t.data); setUsers(u.data); setPerms(p.data);
  };
  useEffect(() => { load(); }, []);

  const canAssign = perms.can_assign_to.length > 0;
  const assignTargets = users.filter((u) => perms.can_assign_to.includes(u.role) && u.user_id !== user.user_id);

  const toggleAssignee = (uid) => {
    setForm((f) => ({
      ...f,
      assignee_ids: f.assignee_ids.includes(uid) ? f.assignee_ids.filter((x) => x !== uid) : [...f.assignee_ids, uid],
    }));
  };

  const create = (e) => {
    e.preventDefault();
    if (!form.title || form.assignee_ids.length === 0) return toast.error("Title + at least 1 assignee required");
    return taskSubmit.run(async () => {
      try {
        await api.post("/tasks", form);
        toast.success(`Task created. ${form.deadline ? "Reminder will be sent 24h before deadline." : ""}`);
        setForm({ title: "", description: "", assignee_ids: [], deadline: "", attachment_url: "" });
        load();
      } catch (e2) { toast.error(e2?.response?.data?.detail || "Failed"); }
    });
  };

  const uploadAttach = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    const r = await api.post("/upload", fd);
    setForm({ ...form, attachment_url: `${BACKEND_URL}${r.data.url}` });
    toast.success("Attached to new task");
  };

  const remove = async (t) => {
    if (!window.confirm("Delete this task and its thread?")) return;
    try {
      await api.delete(`/tasks/${t.task_id}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const toggleMyDone = async (t) => {
    const isDone = (t.completions || {})[user.user_id];
    if (isDone) await api.post(`/tasks/${t.task_id}/uncomplete`);
    else await api.post(`/tasks/${t.task_id}/complete`);
    load();
  };

  const userById = (uid) => users.find((x) => x.user_id === uid);

  const filtered = tasks.filter((t) => {
    const ids = t.assignee_ids || [];
    return view === "assigned-to-me" ? ids.includes(user.user_id) : t.assigned_by === user.user_id;
  });

  const openTask = tasks.find((t) => t.task_id === openTaskId);

  return (
    <Layout navItems={getNav(user.role, customRole)}>
      <div className="p-6 lg:p-10 max-w-7xl mx-auto">
        <PageHeader title="Tasks" subtitle="Assign work, chat in threads, track completion per person" icon={CheckSquare} />

        <div className="flex items-center gap-2 mb-5">
          <button onClick={() => setView("assigned-to-me")} className={`px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2 ${view === "assigned-to-me" ? "brand-gradient text-white" : "bg-white border border-slate-200 text-slate-600"}`} data-testid="tab-assigned-to-me">
            <Inbox className="w-4 h-4" /> Assigned to me ({tasks.filter((t) => (t.assignee_ids || []).includes(user.user_id)).length})
          </button>
          <button onClick={() => setView("assigned-by-me")} className={`px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2 ${view === "assigned-by-me" ? "brand-gradient text-white" : "bg-white border border-slate-200 text-slate-600"}`} data-testid="tab-assigned-by-me">
            <Send className="w-4 h-4" /> Assigned by me ({tasks.filter((t) => t.assigned_by === user.user_id).length})
          </button>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 dashboard-card p-6">
            <div className="space-y-3">
              {filtered.length === 0 && <div className="text-sm text-slate-400 text-center py-8">No tasks here.</div>}
              {filtered.map((t) => {
                const ids = t.assignee_ids || [];
                const completions = t.completions || {};
                const myDone = !!completions[user.user_id];
                const allDone = ids.length > 0 && ids.every((a) => completions[a]);
                const isMyTask = ids.includes(user.user_id);
                const deadlineMs = t.deadline ? new Date(t.deadline).getTime() : null;
                const now = Date.now();
                const duePassed = !!deadlineMs && deadlineMs < now && !allDone;
                const dueSoon = !!deadlineMs && !duePassed && deadlineMs - now < 24 * 3600 * 1000 && !allDone;
                // "Late completion" = all done, but the latest completion happened after the deadline
                const doneAts = Object.values(completions).filter(Boolean);
                const latestDoneMs = doneAts.length ? Math.max(...doneAts.map((s) => new Date(s).getTime())) : 0;
                const lateCompletion = allDone && !!deadlineMs && latestDoneMs && latestDoneMs > deadlineMs;
                return (
                  <div key={t.task_id} className={`p-4 rounded-xl border ${allDone ? (lateCompletion ? "bg-sky-50 border-sky-200" : "bg-emerald-50 border-emerald-200") : duePassed ? "bg-rose-50 border-rose-200" : dueSoon ? "bg-amber-50 border-amber-200" : "border-slate-100"}`} data-testid={`task-${t.task_id}`}>
                    <div className="flex items-start gap-3">
                      {isMyTask && (
                        <button onClick={() => toggleMyDone(t)} className={`mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${myDone ? "bg-emerald-500 border-emerald-500" : "border-slate-300"}`} title="Mark my part complete" data-testid={`task-toggle-${t.task_id}`}>
                          {myDone && <Check className="w-3 h-3 text-white" />}
                        </button>
                      )}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className={`font-semibold ${allDone ? "line-through text-slate-400" : ""}`}>{t.title}</div>
                          {duePassed && <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-500 text-white font-bold" data-testid={`task-badge-due-passed-${t.task_id}`}>Due Passed</span>}
                          {lateCompletion && <span className="text-[10px] px-2 py-0.5 rounded-full bg-sky-500 text-white font-bold" data-testid={`task-badge-late-${t.task_id}`}>Late Completion</span>}
                          {allDone && !lateCompletion && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500 text-white font-bold">Completed</span>}
                        </div>
                        <div className="text-xs text-slate-500 flex items-center gap-2 flex-wrap mt-0.5">
                          <span className="inline-flex items-center gap-1"><UsersIcon className="w-3 h-3" /> {ids.length}</span>
                          {t.deadline && <span className={`inline-flex items-center gap-1 ${duePassed ? "text-rose-700 font-semibold" : dueSoon ? "text-amber-700 font-semibold" : ""}`}><Clock className="w-3 h-3" /> due {t.deadline}</span>}
                          <span>· from {t.assigned_by_name || "—"}</span>
                        </div>
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {ids.map((uid) => {
                            const a = userById(uid);
                            const done = !!completions[uid];
                            return (
                              <span key={uid} className={`text-[10px] px-2 py-0.5 rounded-full ${done ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                                {done && "✓ "}{a?.name?.split(" ")[0] || uid.slice(0, 5)}
                              </span>
                            );
                          })}
                        </div>
                        {t.description && <div className="text-sm text-slate-600 mt-2">{t.description}</div>}
                        {t.attachment_url && <a href={t.attachment_url} target="_blank" rel="noreferrer" className="text-xs text-orange-600 underline mt-1 inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" /> Initial attachment</a>}
                        <div className="mt-2 flex items-center gap-2">
                          <button onClick={() => setOpenTaskId(t.task_id)} className="text-xs text-orange-600 hover:underline inline-flex items-center gap-1" data-testid={`task-open-thread-${t.task_id}`}>
                            <MessageSquare className="w-3 h-3" /> Open thread
                          </button>
                          {(t.assigned_by === user.user_id || user.role === "admin") && (
                            <button onClick={() => remove(t)} className="text-xs text-rose-500 hover:underline inline-flex items-center gap-1">
                              <Trash2 className="w-3 h-3" /> Delete
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {canAssign ? (
            <form onSubmit={create} className="dashboard-card p-6 h-fit space-y-3">
              <h3 className="font-display font-bold text-lg">Assign a task</h3>
              <p className="text-xs text-slate-500">You can assign to: {perms.can_assign_to.map((r) => ROLE_LABELS[r]).join(", ")}</p>

              <input className="input-pill" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="task-title-input" />
              <textarea className="input-pill" rows={2} placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="task-description-input" />

              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">Assign to (pick 1+)</label>
                <div className="max-h-44 overflow-y-auto border border-slate-200 rounded-xl p-2 space-y-1 bg-slate-50/30" data-testid="task-assignee-list">
                  {assignTargets.length === 0 && <div className="text-xs text-slate-400 p-2">No eligible users</div>}
                  {assignTargets.map((u) => {
                    const checked = form.assignee_ids.includes(u.user_id);
                    return (
                      <label key={u.user_id} className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer ${checked ? "bg-orange-100" : "hover:bg-white"}`} data-testid={`task-assignee-opt-${u.user_id}`}>
                        <input type="checkbox" checked={checked} onChange={() => toggleAssignee(u.user_id)} className="rounded" />
                        <span className="text-sm flex-1 truncate">{u.name}</span>
                        <span className="text-[10px] text-slate-500">{ROLE_LABELS[u.role]}</span>
                      </label>
                    );
                  })}
                </div>
                {form.assignee_ids.length > 0 && <div className="text-[11px] text-emerald-600 mt-1">{form.assignee_ids.length} selected</div>}
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">Deadline (used for 24h reminder email)</label>
                <input type="datetime-local" className="input-pill" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} data-testid="task-deadline-input" />
              </div>

              <label className="btn-ghost py-2 text-sm cursor-pointer w-full justify-center">
                <Upload className="w-3 h-3" /> {form.attachment_url ? "Replace attachment" : "Attach file"}
                <input type="file" className="hidden" onChange={(e) => uploadAttach(e.target.files?.[0])} />
              </label>
              {form.attachment_url && <div className="text-xs text-emerald-600">✓ Attachment ready</div>}

              <button type="submit" disabled={taskSubmit.submitting} className="btn-primary w-full py-3 disabled:opacity-60" data-testid="task-create-btn">
                {taskSubmit.submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating…</> : <><Plus className="w-4 h-4" /> Create task</>}
              </button>
            </form>
          ) : (
            <div className="dashboard-card p-6 h-fit">
              <div className="text-sm text-slate-500">Your role can&apos;t assign tasks. You can respond and chat in tasks assigned to you.</div>
            </div>
          )}
        </div>
      </div>

      {openTask && <TaskThreadDrawer task={openTask} onClose={() => setOpenTaskId(null)} users={users} />}
    </Layout>
  );
}

function renderMentions(text) {
  // Highlights any "@Name" token — visual only. Server stores the raw text.
  const parts = String(text || "").split(/(@[\p{L}0-9._-]+(?:\s[\p{L}0-9._-]+)?)/u);
  return parts.map((p, i) => {
    if (p.startsWith("@")) return <span key={i} className="bg-slate-300 text-slate-900 px-1 rounded font-semibold">{p}</span>;
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
}

function TaskThreadDrawer({ task, onClose, users }) {
  const { user, customRole } = useAuth();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [pending, setPending] = useState([]); // attachments to send
  const endRef = useRef(null);

  const load = async () => {
    const r = await api.get(`/tasks/${task.task_id}/messages`);
    setMsgs(r.data);
  };
  useEffect(() => { load(); const i = setInterval(load, 6000); return () => clearInterval(i); }, [task.task_id]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const uploadFile = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    const r = await api.post("/upload", fd);
    const kind = file.type.startsWith("image/") ? "image" : file.type.includes("pdf") ? "pdf" : "file";
    setPending((p) => [...p, { url: `${BACKEND_URL}${r.data.url}`, name: file.name, kind }]);
    toast.success(`${file.name} attached`);
  };

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() && pending.length === 0) return;
    await api.post(`/tasks/${task.task_id}/messages`, { text, attachments: pending });
    setText(""); setPending([]);
    load();
  };

  const removePending = (idx) => setPending((p) => p.filter((_, i) => i !== idx));

  const completions = task.completions || {};
  const ids = task.assignee_ids || [];
  const userById = (uid) => users.find((x) => x.user_id === uid);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-stretch justify-end" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-xl bg-white shadow-2xl flex flex-col" data-testid="task-thread-drawer" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-slate-100 flex items-start gap-3">
          <FileText className="w-5 h-5 text-orange-500 mt-1" />
          <div className="flex-1">
            <div className="font-display font-bold text-lg">{task.title}</div>
            <div className="text-xs text-slate-500">Assigned by {task.assigned_by_name} {task.deadline && `· due ${task.deadline}`}</div>
            <div className="flex flex-wrap gap-1 mt-2">
              {ids.map((uid) => {
                const a = userById(uid);
                const done = !!completions[uid];
                return (
                  <span key={uid} className={`text-[10px] px-2 py-0.5 rounded-full ${done ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                    {done && "✓ "}{a?.name || uid.slice(0, 5)}
                  </span>
                );
              })}
            </div>
          </div>
          <button onClick={onClose}><X className="w-5 h-5 text-slate-400" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 bg-slate-50/30">
          {msgs.length === 0 && <div className="text-center text-slate-400 text-sm mt-12">Start the conversation — share progress, files, or questions.</div>}
          {msgs.map((m) => {
            const mine = m.sender_id === user.user_id;
            return (
              <div key={m.message_id} className={`mb-3 flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] px-4 py-2 rounded-2xl ${mine ? "bg-slate-200 text-slate-800" : "bg-white border border-slate-200 text-slate-800"}`}>
                  <div className="text-[10px] opacity-70 mb-0.5">{m.sender_name} · {m.sender_role}</div>
                  {m.text && <div className="text-sm whitespace-pre-wrap">{renderMentions(m.text)}</div>}
                  {m.attachments && m.attachments.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {m.attachments.map((a, i) => (
                        a.kind === "image" ? (
                          <a key={i} href={a.url} target="_blank" rel="noreferrer"><img src={a.url} alt={a.name} className="max-h-40 rounded-lg" /></a>
                        ) : (
                          <a key={i} href={a.url} target="_blank" rel="noreferrer" className={`block text-xs ${mine ? "text-slate-700" : "text-orange-600"} underline inline-flex items-center gap-1`}>
                            <Paperclip className="w-3 h-3" /> {a.name}
                          </a>
                        )
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          <div ref={endRef} />
        </div>

        {pending.length > 0 && (
          <div className="p-3 border-t border-slate-100 bg-orange-50/40 flex flex-wrap gap-2">
            {pending.map((p, i) => (
              <span key={i} className="inline-flex items-center gap-1 text-xs bg-white px-2 py-1 rounded-full border border-slate-200">
                <Paperclip className="w-3 h-3" /> {p.name}
                <button onClick={() => removePending(i)} className="ml-1 text-slate-400 hover:text-rose-500"><X className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
        )}

        <MentionForm
          value={text}
          onChange={setText}
          onSubmit={send}
          participants={_participantsOf(task, users)}
          onAttach={(f) => uploadFile(f)}
        />
      </div>
    </div>
  );
}

function _participantsOf(task, users) {
  const ids = new Set([...(task.assignee_ids || []), task.assigned_by].filter(Boolean));
  return users.filter((u) => ids.has(u.user_id));
}

function MentionForm({ value, onChange, onSubmit, participants, onAttach }) {
  const [showMentions, setShowMentions] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [caret, setCaret] = useState(0);
  const inputRef = useRef(null);

  const onKeyOrChange = (e) => {
    const el = e.target;
    const val = el.value;
    onChange(val);
    setCaret(el.selectionStart || 0);
    // Look at text up to caret; find the last "@" without a space after it
    const upto = val.slice(0, el.selectionStart || 0);
    const m = upto.match(/@([\p{L}0-9._-]{0,30})$/u);
    if (m) {
      setMentionQuery(m[1]);
      setShowMentions(true);
    } else {
      setShowMentions(false);
    }
  };

  const insertMention = (u) => {
    const el = inputRef.current;
    const upto = value.slice(0, caret);
    const rest = value.slice(caret);
    const replaced = upto.replace(/@([\p{L}0-9._-]{0,30})$/u, `@${u.name.replace(/\s+/g, " ")} `);
    const newVal = replaced + rest;
    onChange(newVal);
    setShowMentions(false);
    setTimeout(() => { el?.focus(); }, 50);
  };

  const filtered = participants.filter((u) => u.name.toLowerCase().includes((mentionQuery || "").toLowerCase())).slice(0, 8);

  return (
    <form onSubmit={onSubmit} className="p-4 border-t border-slate-100 flex gap-2 items-center relative">
      <label className="cursor-pointer p-2 hover:bg-slate-100 rounded-full" title="Attach file">
        <Paperclip className="w-4 h-4 text-slate-500" />
        <input type="file" className="hidden" onChange={(e) => onAttach(e.target.files?.[0])} data-testid="task-thread-attach" />
      </label>
      <input
        ref={inputRef}
        value={value}
        onChange={onKeyOrChange}
        onKeyUp={onKeyOrChange}
        onClick={onKeyOrChange}
        placeholder="Type an update. Use @ to tag a teammate."
        className="input-pill"
        data-testid="task-thread-input"
      />
      {showMentions && filtered.length > 0 && (
        <div className="absolute bottom-16 left-14 right-16 bg-white border border-slate-200 rounded-xl shadow-lg z-20 max-h-56 overflow-y-auto" data-testid="task-mention-dropdown">
          {filtered.map((u) => (
            <button
              key={u.user_id}
              type="button"
              onClick={() => insertMention(u)}
              className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm flex items-center gap-2"
              data-testid={`mention-opt-${u.user_id}`}
            >
              <div className="w-6 h-6 rounded-full bg-slate-200 grid place-items-center text-[10px] font-bold text-slate-700">{u.name.slice(0, 2).toUpperCase()}</div>
              <div className="flex-1">
                <div className="font-semibold text-slate-800 text-sm">{u.name}</div>
                <div className="text-[10px] text-slate-500">{u.role}</div>
              </div>
            </button>
          ))}
        </div>
      )}
      <button type="submit" className="btn-primary px-4" data-testid="task-thread-send"><Send className="w-4 h-4" /></button>
    </form>
  );
}
